import { Typography } from '@frontend/components/ui';
import { Alert } from '@frontend/components/utils';
import { useGetProjectID } from '@frontend/editor/data-access';
import { formatNumberWithCommas } from '@frontend/editor/utils';
import { getTrialPlanDisplay } from './getBillingPlanDisplay';
import {
  useGetBilling,
  useProjectDisconnect,
  useProjectLinked,
} from '@frontend/sorghum/data-access';
import { ThreadsCtx, UICtx } from '@frontend/sorghum/external-providers';
import {
  GetBillingPlanEnum,
  ProjectDisconnectRequest,
  SocialTypeEnum,
} from '@frontend/sorghum/interface';
import { ThreadsIcon } from '@frontend/sorghum/ui';
import {
  THREADS_PERMISSION_EN,
  THREADS_PERMISSION_ZH,
  sendConversionEvent,
  useDocumentTitle,
} from '@frontend/sorghum/utils';
import { Link } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const useThreadsSetting = () => {
  const [t, i18next] = useTranslation('threads');
  const { openCommonModal } = useContext(UICtx);
  const { linkThreads } = useContext(ThreadsCtx);
  const { data: projectID } = useGetProjectID();
  const { mutate: disconnect } = useProjectDisconnect();
  const { data: billing } = useGetBilling();

  const { data: linked, refetch: refetchLinked } = useProjectLinked(
    projectID as string,
  );
  // 連接成功當下「原本是否已連結」：用 ref 讓 BroadcastChannel 的 handler 讀到最新值而不是 effect 建立時的快照
  const wasLinkedRef = useRef(false);
  wasLinkedRef.current = !!linked?.threads?.id;

  const { viewerPermissionUrl } = useMemo(() => {
    if (i18next.language === 'zh_tw') {
      return {
        viewerPermissionUrl: THREADS_PERMISSION_ZH,
      };
    } else {
      return {
        viewerPermissionUrl: THREADS_PERMISSION_EN,
      };
    }
  }, [i18next.language]);

  const openModal = () => linkThreads();

  const disconnectPages = useCallback(() => {
    if (linked?.threads?.id && projectID) {
      const props: ProjectDisconnectRequest = {
        projectId: projectID,
        target: 'threads',
        targetId: linked.threads.id,
      };

      const disconnectThreads = () =>
        disconnect(props, {
          onSuccess: (res) => {
            if (res.code === 20000) {
              Alert.success(t('setting.disconnectModal.successMessage'));
            }
          },
        });

      openCommonModal(
        t('setting.disconnectModal.title'),
        t('setting.disconnectModal.description'),
        disconnectThreads,
        t('setting.disconnectModal.disconnect'),
        'error',
      );
    }
  }, [disconnect, linked?.threads?.id, projectID, t, openCommonModal]);

  const TeamMemberInviteDescription = () => (
    <Typography variant="body1" color="grey.600">
      <Trans i18nKey="setting.inviteDescription">
        You can invite teammates to manage or view your project, learn more
        about the permission access
        <Link
          sx={{ color: 'info.main' }}
          onClick={() => window.open(viewerPermissionUrl, '_blank')}
        >
          {t('setting.inviteLink')}
        </Link>
      </Trans>
    </Typography>
  );

  const connectedPageLink = `https://www.threads.net/@${linked?.threads?.linkId}`;

  const connectedPageName = linked?.threads?.name;
  const accountName = linked?.threads?.accountName;

  const Icon = () => <ThreadsIcon size="xl" />;

  const socialType = SocialTypeEnum.THREADS;
  const socialTypeName = t('dataDashboard.threads');

  const hasBusinessAccountDescription = false;

  const { planTitle, price, period } = useMemo(() => {
    let planTitle = '';
    let price = '-';
    let period = '';

    if (billing) {
      switch (billing.plan) {
        case GetBillingPlanEnum.TRIAL: {
          // 緩衝期顯示邏輯共用 getTrialPlanDisplay（2026-08 客訴 UX 修正）
          ({ planTitle, price, period } = getTrialPlanDisplay(billing, t, {
            planTitle: t('setting.payment.trial'),
            price: t('setting.payment.free'),
            period: `/${t('setting.payment.month')}`,
          }));
          break;
        }
        case GetBillingPlanEnum.ADVANCE: {
          planTitle = t('setting.payment.advance');
          price = `${
            billing.country === 'TW' ? 'NT$' : 'US$'
          } ${formatNumberWithCommas(billing?.amount ?? 0)}`;
          period = `/${
            billing?.period === 1
              ? t('setting.payment.month')
              : t('setting.payment.year')
          }`;
          break;
        }
        case GetBillingPlanEnum.BETA: {
          planTitle = t('setting.payment.betaVersion');
          price = t('setting.payment.free');
          period = `/${t('setting.payment.month')}`;
          break;
        }
        default: {
          planTitle = t('setting.payment.viewMode');
          price = '-';
          period = '';
        }
      }
    }

    return {
      price,
      period,
      planTitle,
    };
  }, [billing, t]);

  useEffect(() => {
    if (projectID) {
      const bc = new BroadcastChannel('threads_connect');
      bc.onmessage = (event) => {
        refetchLinked();
        if (event.data.isSuccess) {
          Alert.success(t('setting.connectSuccess'));
          // 在主視窗而不是 OAuth popup 送轉換事件：popup 關太快，GTM 來不及送出。
          // 只算「首次連接」（啟用訊號）：客服請客戶重新授權同一帳號的重連不送，避免灌水
          if (!wasLinkedRef.current) {
            sendConversionEvent('channel_connected', { channel: 'threads' });
          }
        } else {
          // 使用 redirect popup 傳回的具體訊息（例如「已連結至其他專案」），沒有才退回 generic
          Alert.error(event.data.message || t('setting.connectFailed'));
        }
      };

      return () => {
        bc.close();
      };
    }
    return;
  }, [t, projectID, refetchLinked]);

  useDocumentTitle(t('title.setting'));

  return {
    linked,
    isConnect: !!linked?.threads?.id,
    refreshPage: () => null,
    disconnectPages,
    openModal,
    TeamMemberInviteDescription,
    Icon,
    accountName,
    connectedPageLink,
    connectedPageName,
    socialType,
    socialTypeName,
    planTitle,
    price,
    period,
    billing,
    hasBusinessAccountDescription,
  };
};
